import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { Sport } from './Sport';
import type { EntityState } from './../enums/EntityState';

export class Federation extends BaseObj {
    public ID: number;
    public __id: number;

    public CountryID: number;
    public __countryid: number;

    public LongName: string | null;
    public __longname: string | null;

    public Name: string;
    public __name: string;

    public ShortName: string | null;
    public __shortname: string | null;

    public Sports: Sport[];
    public __sports: Sport[];

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        CountryID?: number;
        EntityState?: EntityState;
        LongName?: string | null;
        Name?: string | null;
        ShortName?: string | null;
        Sports?: Sport[];
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Federation>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Federation tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CountryID = values.CountryID ?? 0;
        this.__countryid = values.CountryID ?? 0;
        Object.defineProperty(this, 'CountryID', {
            get(): number {
                return this.__countryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('CountryID', this.__countryid, value);
                this.__countryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LongName = values.LongName ?? '';
        this.__longname = values.LongName ?? '';
        Object.defineProperty(this, 'LongName', {
            get(): string | null {
                return this.__longname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LongName', this.__longname, value);
                this.__longname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShortName = values.ShortName ?? '';
        this.__shortname = values.ShortName ?? '';
        Object.defineProperty(this, 'ShortName', {
            get(): string | null {
                return this.__shortname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ShortName', this.__shortname, value);
                this.__shortname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sports = values.Sports ?? [];
        this.__sports = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Sports ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('Sports', newVal),
        });
        this.__sports.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Sports', this.Sports),
            );
        });
        Object.defineProperty(this, 'Sports', {
            get(): Sport[] {
                return this.__sports;
            },
            set(value: Sport[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__sports = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: Sport[], newVal: Sport[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Sports', newVal),
                });
                this.__sports.forEach((child: Sport) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Sports', this.Sports),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Sports', this.Sports);
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CountryID": ${JSON.stringify(this.CountryID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"LongName": ${JSON.stringify(this.LongName)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"ShortName": ${JSON.stringify(this.ShortName)}` +
            ',' +
            `"Sports": [ ${this.Sports.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Federation from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CountryID?: number;
        LongName?: string | null;
        Name?: string | null;
        ShortName?: string | null;
        Sports?: Sport[];
        EntityState?: 'Added' | 'Unchanged';
    }): Federation {
        return new Federation(values);
    }

    public static _FromServerJson(json: any): Federation {
        return new Federation({
            ID: json.ID,
            CountryID: json.CountryID,
            LongName: json.LongName ?? '',
            Name: json.Name ?? '',
            ShortName: json.ShortName ?? '',
            Sports: json.Sports?.map((x: any) => Sport._FromServerJson(x)),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Federation from another instance of Federation.
     * @param other The Federation you want to clone
     * @returns A cloned Federation
     */
    public static FromOther(other: Federation): Federation {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Federation._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Federation.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'CountryID' | 'LongName' | 'Name' | 'ShortName' | 'Sports')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Federation';
}
